import * as React from 'react';
import EventListener from '../EventListener';
import {RevealProps, RevealState, RevealRenderFnProps} from './types';

// TODO: add docs
export default class Reveal extends React.Component<RevealProps, RevealState> {
  constructor(props: RevealProps) {
    super(props);
    this.state = {
      revealState: props.visible || props.defaultVisible ? 'revealed' : 'dismissed'
    };
  }

  public componentWillReceiveProps({visible}: RevealProps) {
    if (this._isControlled) {
      visible ? this._show() : this._hide();
    }
  }

  public render() {
    const {children: _children, dismissOnEsc, forceRender} = this.props;
    const children = _children instanceof Function ? _children(this._renderFunctionProps) : _children;

    const eventListener =
      this.isRevealed && dismissOnEsc ? (
        <EventListener entries={[{target: 'document', on: 'keydown', handler: this._handleDocumentKeydown}]} />
      ) : null;

    return this.isVisible || forceRender ? (
      <React.Fragment>
        {eventListener}
        {children}
      </React.Fragment>
    ) : null;
  }

  public toggle = () => {
    if (this.state.revealState === 'revealed') {
      this.dismiss();
      return;
    }
    if (this.state.revealState === 'dismissed') {
      this.reveal();
    }
  };

  public reveal = () => {
    if (typeof this.props.onReveal === 'function') {
      this.props.onReveal();
    }
    if (!this._isControlled) {
      this._show();
    }
  };

  public dismiss = () => {
    if (typeof this.props.onDismiss === 'function') {
      this.props.onDismiss();
    }
    if (!this._isControlled) {
      this._hide();
    }
  };

  public get isVisible() {
    return this.state.revealState !== 'dismissed';
  }

  public get isRevealed() {
    return this.state.revealState === 'revealing' || this.state.revealState === 'revealed';
  }

  private get _isControlled() {
    return this.props.visible !== undefined;
  }

  private get _renderFunctionProps(): RevealRenderFnProps {
    return {
      revealState: this.state.revealState,
      isVisible: this.isVisible,
      isRevealed: this.isRevealed,
      toggle: this.toggle,
      reveal: this.reveal,
      dismiss: this.dismiss
    };
  }

  private _show = () => {
    if (this.state.revealState === 'dismissed') {
      this._transitionalSetState(
        {revealState: 'revealing'},
        {revealState: 'revealed'},
        this._getTransitionDuration('reveal'),
        {
          transition: this.props.onRevealTransition,
          after: this.props.onAfterReveal
        }
      );
    }
  };

  private _hide = () => {
    if (this.state.revealState === 'revealed') {
      this._transitionalSetState(
        {revealState: 'dismissing'},
        {revealState: 'dismissed'},
        this._getTransitionDuration('dismiss'),
        {
          transition: this.props.onDismissTransition,
          after: this.props.onAfterDismiss
        }
      );
    }
  };

  private _transitionalSetState = (
    transitionState: RevealState,
    finalState: RevealState,
    duration?: number,
    callback?: {transition?: () => void; after?: () => void}
  ) => {
    const tryCb = (on: 'transition' | 'after') => {
      if (callback && typeof callback[on] === 'function') {
        callback[on]!();
      }
    };

    if (!duration || duration <= 0) {
      this.setState(finalState, () => {
        tryCb('after');
      });
      return;
    }

    this.setState(transitionState, () => {
      tryCb('transition');
    });

    setTimeout(() => {
      this.setState(finalState, () => {
        tryCb('after');
      });
      // tslint:disable-next-line:align
    }, duration);
  };

  private _getTransitionDuration(on: 'reveal' | 'dismiss'): number | undefined {
    const {transitionDuration: duration} = this.props;
    return duration ? duration[on] || duration : undefined;
  }

  private _handleDocumentKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' || event.keyCode === 27) {
      this.dismiss();
    }
  };
}



// WEBPACK FOOTER //
// ./src/components/Reveal/Reveal.tsx